import { apiFailureFromResponse } from "../../utils/api/parseApiErrorMessage";
import { ApiClient } from "../ApiClient";

const PLAID_CONNECT_ENDPOINT = "finances/plaid/connect/";

/**
 * Initializes Plaid Link by returning a link_token.
 * Components expect the JSON object directly to access .link_token
 */
export async function createPlaidLinkToken() {
  const res = await ApiClient.post(PLAID_CONNECT_ENDPOINT);
  return await res.json();
}

/**
 * Exchanges public_token for access_token.
 * Returns the JSON response to update UI state.
 */
export async function exchangePlaidPublicToken(publicToken) {
  const res = await ApiClient.post(PLAID_CONNECT_ENDPOINT, {
    public_token: publicToken,
  });
  return await res.json();
}

/**
 * All PUT actions below return JSON data for the UI to handle redirects or status updates.
 */

export async function createPlaidInstitutionUpdateLinkToken(institutionUuid) {
  const res = await ApiClient.put(
    `${PLAID_CONNECT_ENDPOINT}${encodeURIComponent(institutionUuid)}/`,
    { action: "update" },
  );
  return await res.json();
}

export async function createPlaidInstitutionReconnectLinkToken(
  institutionUuid,
) {
  const res = await ApiClient.put(
    `${PLAID_CONNECT_ENDPOINT}${encodeURIComponent(institutionUuid)}/`,
    { action: "reconnect" },
  );
  return await res.json();
}

export async function refreshPlaidInstitutionAccounts(institutionUuid) {
  const res = await ApiClient.put(
    `${PLAID_CONNECT_ENDPOINT}${encodeURIComponent(institutionUuid)}/`,
    { action: "refresh_accounts" },
  );
  return await res.json();
}

export async function unlinkPlaidInstitution(institutionUuid) {
  const res = await ApiClient.put(
    `${PLAID_CONNECT_ENDPOINT}${encodeURIComponent(institutionUuid)}/`,
    { action: "deactivate" },
  );
  return await res.json();
}

/**
 * Deletes the institution.
 * Even if the body is empty, we parse JSON to catch success/error messages.
 */
export async function removePlaidInstitution(institutionUuid) {
  const res = await ApiClient.delete(
    `${PLAID_CONNECT_ENDPOINT}${encodeURIComponent(institutionUuid)}/`,
  );
  try {
    return await res.json();
  } catch {
    return { success: res.ok };
  }
}

/**
 * Fetch all linked financial institutions and their nested accounts
 * Globally accessible source of truth for tracking configurations (Budgets, Guardian, etc.)
 */
export async function fetchInstitutionsAccounts() {
  const response = await ApiClient.get(PLAID_CONNECT_ENDPOINT);

  if (response.ok) {
    const data = await response.json();
    return { success: true, data };
  }

  return apiFailureFromResponse(
    response,
    "Failed to load connected tracking sources",
  );
}

/**
 * Fetches available mock institutions for test accounts.
 */
export async function fetchMockInstitutions() {
  const res = await ApiClient.get("finances/mock/institutions");
  return await res.json();
}

/**
 * Connects/clones a mock institution to the test account.
 */
export async function connectMockInstitution(institutionUuid) {
  const res = await ApiClient.post("finances/mock/connection", {
    institution_uuid: institutionUuid,
  });
  return await res.json();
}

/**
 * Un-links/disconnects a mock institution for a test account.
 */
export async function removeMockInstitution(institutionUuid) {
  const res = await ApiClient.delete(
    `finances/mock/disconnect/${encodeURIComponent(institutionUuid)}`,
  );
  try {
    return await res.json();
  } catch {
    return { success: res.ok };
  }
}
